// src/components/Groups/MemberRow.jsx
import React, { useState } from 'react';
import { Button, Dropdown, Badge } from 'react-bootstrap';
import { Crown, Shield, Edit3, Eye, UserMinus, MoreVertical } from 'lucide-react';

const ROLES = ['viewer', 'editor', 'admin', 'owner'];

const ROLE_META = {
  owner:  { icon: <Crown size={14} />,  variant: 'warning', label: 'Owner' },
  admin:  { icon: <Shield size={14} />, variant: 'info', label: 'Admin' },
  editor: { icon: <Edit3 size={14} />,  variant: 'success', label: 'Editor' },
  viewer: { icon: <Eye size={14} />,    variant: 'secondary', label: 'Viewer' },
};

const MemberRow = ({
  membership,
  currentUserRole,
  currentUserId,
  onChangeRole,
  onRemove,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const { user_details: user, role } = membership;
  const isSelf = String(user?.id) === String(currentUserId);
  const canManage =
    !isSelf &&
    (currentUserRole === 'owner' ||
      (currentUserRole === 'admin' && role !== 'owner'));

  const roleMeta = ROLE_META[role] || ROLE_META.viewer;

  const getAvailableRoles = () => {
    return ROLES.filter((r) => {
      // Don't show current role
      if (r === role) return false;
      // Only owners can assign owner role
      if (r === 'owner' && currentUserRole !== 'owner') return false;
      return true;
    });
  };

  const handleRoleChange = (newRole) => {
    onChangeRole(user.id, newRole);
    setDropdownOpen(false);
  };

  const handleRemove = () => {
    if (window.confirm(`Remove ${user?.full_name || user?.email} from the group?`)) {
      onRemove(user.id);
    }
    setDropdownOpen(false);
  };

  // Common content for both views
  const renderAvatar = () => (
    <div className="member-row__avatar">
      {user?.avatar_url ? (
        <img src={user.avatar_url} alt={user.full_name} />
      ) : (
        <div className="member-row__avatar-placeholder">
          {(user?.full_name || user?.email || '?').charAt(0).toUpperCase()}
        </div>
      )}
    </div>
  );

  const renderUserInfo = () => (
    <div className="member-row__info">
      <span className="member-row__name">
        {user?.full_name || user?.email}
        {isSelf && <span className="member-row__you"> (you)</span>}
      </span>
      <span className="member-row__email">{user?.email}</span>
    </div>
  );

  const renderRoleBadge = () => (
    <Badge
      bg={roleMeta.variant}
      className="d-flex align-items-center gap-1 px-2 py-1"
      style={{ fontSize: '0.7rem' }}
    >
      {roleMeta.icon}
      <span>{roleMeta.label}</span>
    </Badge>
  );

  // Non-manageable view (regular member)
  if (!canManage) {
    return (
      <div className="member-row">
        {renderAvatar()}
        {renderUserInfo()}
        {renderRoleBadge()}
      </div>
    );
  }

  // Manageable view (admin/owner)
  return (
    <div className="member-row">
      {renderAvatar()}
      {renderUserInfo()}
      {renderRoleBadge()}

      <div className="member-row__actions">
        <Dropdown
          align="end"
          show={dropdownOpen}
          onToggle={(isOpen) => setDropdownOpen(isOpen)}
        >
          <Dropdown.Toggle
            as={Button}
            variant="link"
            size="sm"
            className="icon-btn p-1"
            style={{ textDecoration: 'none' }}
          >
            <MoreVertical size={16} />
          </Dropdown.Toggle>

          <Dropdown.Menu className="shadow-sm">
            <Dropdown.Header>Change role to:</Dropdown.Header>
            {getAvailableRoles().map((r) => {
              const roleInfo = ROLE_META[r];
              return (
                <Dropdown.Item
                  key={r}
                  onClick={() => handleRoleChange(r)}
                  className="d-flex align-items-center gap-2"
                >
                  {roleInfo.icon}
                  <span>{roleInfo.label}</span>
                </Dropdown.Item>
              );
            })}
            <Dropdown.Divider />
            <Dropdown.Item
              onClick={handleRemove}
              className="d-flex align-items-center gap-2 text-danger"
            >
              <UserMinus size={14} />
              <span>Remove from group</span>
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </div>
  );
};

export default MemberRow;